import React from "react";
import type { RouteObject } from "react-router";
import type {
  BaseRouteItem,
  NavRouteItem,
  NavRouteTree,
  NestedNavRouteMenu,
  Role,
} from "./types";
import { isExcludedEnv } from "@/helpers/switch.helpers";
import ExternalPage from "../pages/ExternalPage";
import navRouteTree from "./navRouteTree";

/**
 * Flatten the nested NavMenuTree to single array of routable items
 */
export const flattenToRoutes = (tree: NavRouteTree): NavRouteItem[] => {
  return tree.reduce((acc: NavRouteItem[], item) => {
    if (isParentItem(item)) {
      // Add the parent route if it has a path
      if ("path" in item) {
        acc.push(item as NavRouteItem);
      }
      // Add all children
      acc.push(...item.children);
      // Recursively add any nested children
      item.children.forEach((child) => {
        if (isParentItem(child)) {
          acc.push(...flattenToRoutes([child]));
        }
      });
    } else {
      acc.push(item);
    }
    return acc;
  }, []);
};

/**
 * A lookup table for nav items keyed on the route path.
 *
 * Note: use `navItemsMap` constant. This function is exported only for tests
 */
export const buildNavItemsMap = (tree: NavRouteTree) =>
  flattenToRoutes(tree).reduce(
    (acc: Record<string, NavRouteItem>, item: NavRouteItem) => {
      acc[item.path] = item;
      return acc;
    },
    {}
  );

/**
 * Checks if user has appropriate permissions.
 * @param userRole list of roles user has
 * @param roles list of roles that are allowed to visit the route in question
 */
export const userHasPermission = (userRole: Role[], roles: Role[]) =>
  userRole.some((role) => roles.includes(role));

export const isExcludedEnvRoute = (navItem: BaseRouteItem) =>
  isExcludedEnv(navItem.excludedEnvs);

const excludeItemPartial =
  (userRole: Role[]) => (item: NavRouteItem | NestedNavRouteMenu) => {
    return (
      !item.exclude &&
      !isExcludedEnvRoute(item) &&
      userHasPermission(userRole, item.roles)
    );
  };

/**
 * Type guard for NestedNavRouteMenu
 */
export const isParentItem = (
  item: NavRouteItem | NestedNavRouteMenu
): item is NestedNavRouteMenu =>
  (item as NestedNavRouteMenu).children !== undefined;

/**
 * Filter tree tree to remove excluded items
 */
export const filterExcludedNavItems = (
  tree: NavRouteTree,
  userRole: Role[]
) => {
  const predicate = excludeItemPartial(userRole);
  return tree.filter(predicate).map((item) =>
    isParentItem(item)
      ? {
          ...item,
          children: item.children.filter(predicate),
        }
      : item
  );
};

/**
 * Finds the active nav section for a given path.
 */
export const findParentForPath = (
  tree: NavRouteTree,
  path: string
): NestedNavRouteMenu | undefined => {
  for (const parent of tree) {
    if (isParentItem(parent)) {
      if (parent.children.some((item) => path.startsWith(item.path))) {
        return parent;
      }
    }
  }
};

export const navItemsMap = buildNavItemsMap(navRouteTree);

/**
 * Creates a RouteObject with an ExternalPage component
 * keyed by path to ensure a new instance.
 */
export const buildExtRoute = (path: keyof typeof navItemsMap): RouteObject => {
  const { isExternal, label, allowWildcard } = navItemsMap[path];

  if (!isExternal) throw Error(`${path} is not an external route`);

  return {
    path: allowWildcard ? `${path}/*` : path,
    element: <ExternalPage key={path} path={path} title={label} />,
  };
};

/**
 * Returns a matching srcPath for navItems or the given path
 */
export const getExternalSrcPath = (path: string): string => {
  const item = navItemsMap[path];
  return item?.isExternal ? item.srcPath : path;
};

const base = String(import.meta.env.VITE_ROUTER_BASE);
const baseRE = new RegExp(`^${base}`);

/**
 * Returns a path stripped of the router base "/app"
 **/
export const getPathFromLocation = (pathname = location.pathname) =>
  base ? pathname.replace(baseRE, "") : pathname;

export const getUrlFromPath = (path: string) =>
  `${base}${getPathFromLocation(path)}`;
